import dayjs from "dayjs";
import { format as formatDate, isValid, parse } from "date-fns";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
// Indonesian month names are the default output of this module, so it registers
// the locale itself instead of trusting App.js to have done it — otherwise every
// consumer outside the app entry point (tests, embeds) silently gets English.
import "dayjs/locale/id";
import { getApiErrorBody } from "./StringUtils";

// A closed-period boundary is a WIB month-end stored as a UTC instant
// (2025-06-30T16:59:59Z === 30 Jun 23:59:59 WIB), so anything that reads the
// boundary as a *calendar date* has to do it in WIB or the day flips. Extending
// the plugins here rather than relying on some unrelated module having already
// done it to the shared dayjs singleton — same reasoning as useServiceAvailability.
dayjs.extend(utc);
dayjs.extend(timezone);

export const WIB = "Asia/Jakarta";

export const PERIOD_CLOSED = "PERIOD_CLOSED";

/**
 * Earliest date the user may still write to.
 *
 * `closedThrough` is the last CLOSED instant, so the first writable day is the
 * WIB day containing `closedThrough + 1s`. Using `.add(1, "day")` instead would
 * skip 1 July entirely and cost the user a whole day.
 *
 * The result is rebuilt as a *local*-midnight Date from the WIB calendar Y/M/D,
 * because react-datepicker compares `minDate` against local-midnight Dates.
 * For a WIB browser (effectively everyone) that is exactly midnight 1 Jul.
 *
 * Returns `undefined` — not null — when the book is open, so callers can write
 * `<FormDate minDate={minDate} />` with no conditional and get "no constraint".
 */
export const getPeriodMinDate = (closedThrough) => {
  if (!closedThrough) return undefined;

  const boundary = dayjs(closedThrough);
  if (!boundary.isValid()) return undefined;

  const firstOpen = boundary.add(1, "second").tz(WIB);
  const candidate = new Date(
    firstOpen.year(),
    firstOpen.month(),
    firstOpen.date(),
  );

  // A browser east of WIB can land on a local midnight whose instant is still
  // inside the closed period. Step forward so minDate never offers a day the
  // server would reject.
  if (candidate.getTime() <= boundary.valueOf()) {
    return new Date(firstOpen.year(), firstOpen.month(), firstOpen.date() + 1);
  }

  return candidate;
};

/**
 * Is this record or picker value inside the closed period?
 *
 * A plain instant comparison is exactly right here — `closedThrough` *is* the
 * boundary instant. A server-sourced ISO string compares correctly, and a Date
 * from a picker compares as the instant `toISOString()` will actually send,
 * which is the value the server evaluates. So the client guard agrees with the
 * server rather than contradicting it.
 */
export const isDateInClosedPeriod = (date, closedThrough) => {
  if (!closedThrough || !date) return false;

  const value = dayjs(date);
  const boundary = dayjs(closedThrough);
  if (!value.isValid() || !boundary.isValid()) return false;

  return value.valueOf() <= boundary.valueOf();
};

/**
 * The closed-period date typed into a react-datepicker field, or null.
 *
 * The picker drops a typed date it has disabled (below `minDate`) without a
 * word, so the form stayed empty and said "date required" instead of why.
 * Handing this date to the form's own onChange lets its period check name the
 * closed period, the same as for a stored date. Only a complete date counts —
 * "15/09/202" already parses, as the year 202 — and `pattern` is the picker's
 * date-fns `dateFormat`; a date-and-time field also takes the date alone.
 */
export const parseTypedClosedDate = (text, pattern, isDateClosed) => {
  if (typeof text !== "string" || typeof pattern !== "string") return null;
  if (!isDateClosed) return null;

  const patterns = [...new Set([pattern, pattern.split(" ")[0]])];
  for (const candidate of patterns) {
    const typed = parse(text, candidate, new Date());
    if (!isValid(typed) || formatDate(typed, candidate) !== text) continue;
    return isDateClosed(typed) ? typed : null;
  }
  return null;
};

/** "30 Juni 2025" — always the WIB calendar date, never the browser's. */
export const formatPeriodDate = (value, locale = "id") => {
  if (!value) return "";

  const d = dayjs(value).tz(WIB);
  return d.isValid() ? d.locale(locale).format("DD MMMM YYYY") : "";
};

/**
 * "30 Juni 2025 · 22:34" — when a close or reopen was *performed*.
 *
 * Kept apart from formatPeriodDate on purpose. That one renders `closedThrough`,
 * which is a period boundary and has no clock; this one renders an audit event,
 * where two closes on the same day are otherwise indistinguishable. Both pin to
 * WIB, so the log reads in the books' own zone rather than the browser's.
 */
export const formatPeriodDateTime = (value, locale = "id") => {
  if (!value) return "";

  const d = dayjs(value).tz(WIB);
  return d.isValid() ? d.locale(locale).format("DD MMMM YYYY · HH:mm") : "";
};

/** "Juli 2025" — for labelling a close candidate by the month it closes. */
export const formatPeriodMonth = (value, locale = "id") => {
  if (!value) return "";

  const d = dayjs(value).tz(WIB);
  return d.isValid() ? d.locale(locale).format("MMMM YYYY") : "";
};

/** Picker Date (or ISO) -> the WIB calendar date to send as `through`. */
export const toPeriodThrough = (date) => {
  if (!date) return undefined;

  const d = dayjs(date).tz(WIB);
  return d.isValid() ? d.format("YYYY-MM-DD") : undefined;
};

/**
 * Picker Date (or ISO) -> the WIB month it falls in, as "YYYY-MM".
 *
 * A tax period (masa pajak) is a WIB calendar month, so a bill dated 1 Nov
 * 05:00 WIB belongs to November even though its UTC instant is still 31 Oct.
 */
export const toWibPeriod = (date) => {
  if (!date) return null;

  const d = dayjs(date).tz(WIB);
  return d.isValid() ? d.format("YYYY-MM") : null;
};

// The calendar day the user picked, as "YYYY-MM-DD". A picker hands back local
// midnight of the day it shows, so the browser's own calendar is the one to
// read here - that is the day on screen, wherever the laptop is.
const pickedDay = (date) => {
  if (!date) return null;
  const d = dayjs(date);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
};

/**
 * 00:00:00.000 WIB of the picked day, as the ISO instant a report sends.
 *
 * The books close on WIB days, so a range built from the laptop's clock
 * shifted every boundary for a user in WITA, WIT or UTC: an expense at 05:00
 * WIB on 1 October fell into September. October in any zone is
 * 2026-09-30T17:00:00.000Z .. 2026-10-31T16:59:59.999Z.
 */
export const wibStartOfDay = (date) => {
  const day = pickedDay(date);
  return day ? dayjs.tz(`${day}T00:00:00.000`, WIB).toISOString() : undefined;
};

/** 23:59:59.999 WIB of the picked day, as an ISO instant. */
export const wibEndOfDay = (date) => {
  const day = pickedDay(date);
  return day ? dayjs.tz(`${day}T23:59:59.999`, WIB).toISOString() : undefined;
};

/** `{ startDate, endDate }` for a picked range, both WIB day boundaries. */
export const wibDateRange = (start, end) => ({
  startDate: wibStartOfDay(start),
  endDate: wibEndOfDay(end),
});

/** The WIB month the picked day falls in, as `{ startDate, endDate }`. */
export const wibMonthRange = (date) => {
  const d = dayjs(date);
  if (!date || !d.isValid()) return { startDate: undefined, endDate: undefined };
  return wibDateRange(d.startOf("month"), d.endOf("month"));
};

/** The WIB calendar year the picked day falls in, as `{ startDate, endDate }`. */
export const wibYearRange = (date) => {
  const d = dayjs(date);
  if (!date || !d.isValid()) return { startDate: undefined, endDate: undefined };
  return wibDateRange(d.startOf("year"), d.endOf("year"));
};

/**
 * Today's WIB date as the local-midnight Date a picker shows.
 *
 * "Bulan ini" opened at 01:00 WIB on 1 November from a laptop in UTC is still
 * 31 October there; the books are already in November.
 */
export const wibToday = (now = new Date()) => toWibPickerDate(now);

/**
 * An instant (an ISO from a URL or the server) as the picker Date of its WIB
 * day. `new Date("2026-09-30T17:00:00.000Z")` is 1 October in WIB but 30
 * September on a laptop in UTC, so a report link would open a day early.
 */
export const toWibPickerDate = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  const wib = d.tz(WIB);
  return new Date(wib.year(), wib.month(), wib.date());
};

/** First and last day of the current WIB month, as picker Dates. */
export const wibCurrentMonth = (now = new Date()) => {
  const today = wibToday(now);
  return [
    new Date(today.getFullYear(), today.getMonth(), 1),
    new Date(today.getFullYear(), today.getMonth() + 1, 0),
  ];
};

/** The current WIB month as `{ startDate, endDate }` ISO instants. */
export const wibThisMonth = (now = new Date()) => wibMonthRange(wibToday(now));

/**
 * The current and previous WIB month, in the shape the /stats endpoints take.
 * They used to get dayjs objects, serialised as the laptop's month bounds.
 */
export const wibStatsMonths = (now = new Date()) => {
  const today = wibToday(now);
  const current = wibMonthRange(today);
  const previous = wibMonthRange(
    new Date(today.getFullYear(), today.getMonth() - 1, 1),
  );
  return {
    startDate: current.startDate,
    endDate: current.endDate,
    prevStartDate: previous.startDate,
    prevEndDate: previous.endDate,
  };
};

/**
 * The last day of the most recent fiscal year that has ended, as a picker Date.
 *
 * An opening balance is stated as of the close of the previous fiscal year, so
 * a business whose year starts in July opens on 30 June, not 31 December.
 * `fiscalYearStartMonth` is 1-12; empty means January. Today is read in WIB,
 * and on the first day of a fiscal year the year that just ended is the one.
 * On 30 Oct 2026: January -> 31 Dec 2025, April -> 31 Mar 2026, July -> 30 Jun
 * 2026.
 */
export const getLastFiscalYearEnd = (fiscalYearStartMonth, now = new Date()) => {
  const startMonth = Number(fiscalYearStartMonth);
  const month = startMonth >= 1 && startMonth <= 12 ? startMonth : 1;
  const today = wibToday(now);
  const startYear =
    today.getMonth() + 1 >= month ? today.getFullYear() : today.getFullYear() - 1;
  // Day 0 of the start month is the last day of the month before it.
  return new Date(startYear, month - 1, 0);
};

/**
 * The period message as display lines: one per sentence.
 *
 * The backend writes two sentences — what happened, then what to do about it —
 * and inside a table cell that reads as one very long line that drags the
 * column past the viewport. Breaking at the sentence boundary is a rendering
 * decision, so it lives here rather than in the wording: the string itself is
 * still the backend's, untouched.
 *
 * Split without lookbehind on purpose — Safari only gained it in 16.4, and this
 * runs on whatever phone the user has.
 */
export const toPeriodMessageLines = (message) => {
  if (typeof message !== "string") return [];

  const parts = message.split(". ");
  return parts
    .map((part, index) =>
      // Every part but the last lost its full stop to the split.
      index < parts.length - 1 ? `${part.trim()}.` : part.trim(),
    )
    .filter(Boolean);
};

// The writer families tag PERIOD_CLOSED at three different depths:
//   any writer                       409  code, closedThrough (body top level)
//   /transaction, /opening-balance   409  error: ["PERIOD_CLOSED"]
//   /invoice                         409  error: { code, closedThrough }
// The top-level form is the one the lock middleware emits, so it can accompany
// (or replace) any of the others depending on which layer rejected the write.
// Flattening every leaf string is the only shape-agnostic way to spot the tag.
//
// The row-wise writers are deliberately NOT in that list. /bulk/transaction (400)
// and /upload/transaction (200) name the offending rows in a top-level
// `closedRows: [0, 2]` and leave `error` holding nothing but the ordinary
// per-field messages — the tag is never buried inside it, precisely so
// getErrorMessages() cannot print "PERIOD_CLOSED" at a user.
const flattenStrings = (value) => {
  if (!value) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(flattenStrings);
  if (typeof value === "object") return Object.values(value).flatMap(flattenStrings);
  return [];
};

/**
 * Per-row map for the row-wise writers: `{ [rowIndex]: message | null }`.
 *
 * `closedRows` is the authority on *which* rows were rejected — the row numbers
 * come from the server, not from pattern-matching its prose. The message is then
 * read out of `error.transactions[i].created_at`, which is where the period
 * explanation lands when there is one; a row listed with no message maps to
 * `null`, so callers must test `rows[i] !== undefined` rather than truthiness.
 *
 * The message passes through verbatim — it is already user-facing Indonesian
 * naming the boundary date and the recovery step, and the backend chose that
 * wording, not us.
 *
 * Both entry points share this so the shape is understood in exactly one place:
 * /upload/transaction answers 200 with the body intact, /bulk/transaction throws
 * a 400 that still has to be unwrapped first.
 */
export const buildPeriodClosedRows = (closedRows, errorField) => {
  if (!Array.isArray(closedRows)) return {};

  const rows = {};
  closedRows.forEach((key) => {
    // Number(null) and Number("") are both 0, so a junk entry would silently
    // mark the *first* row — the one most likely to be innocent. Only a number
    // or a non-empty numeric string is a row reference.
    if (typeof key !== "number" && typeof key !== "string") return;
    if (typeof key === "string" && key.trim() === "") return;

    const index = Number(key);
    if (!Number.isInteger(index)) return;

    // Object keys arrive as strings ("0"), which numeric property access
    // resolves correctly for both an object and an array.
    const messages = flattenStrings(
      errorField?.transactions?.[index]?.created_at,
    );
    rows[index] = messages.find((message) => message !== PERIOD_CLOSED) || null;
  });

  return rows;
};

/**
 * The same map, from a thrown HTTP error.
 *
 * A body without `closedRows` yields {} — which is what a server that has not
 * been deployed yet produces, and leaves the page exactly as it was before this
 * feature rather than guessing at rows.
 */
export const getPeriodClosedRows = (error) => {
  const body = getApiErrorBody(error);
  return buildPeriodClosedRows(body?.closedRows, body?.error);
};

/**
 * Normalizes any writer's period rejection to one shape, or null when the error
 * is something else entirely.
 *
 * `message` is never rewritten, only chosen between. A row message wins over
 * `body.message`: the bulk endpoint sends the generic "Data tidak valid" at the
 * top level — honest for a batch that can fail several ways at once, but
 * useless as the single line a notice shows. The row message names the boundary
 * date and the recovery step ("Buat jurnal koreksi pada periode berjalan atau
 * buka kembali periode tersebut"). Single-writer 409s carry no rows, so they
 * still fall through to `body.message`, which is already that sentence there.
 */
export const getPeriodClosedError = (error) => {
  const body = getApiErrorBody(error);
  if (!body) return null;

  const err = body.error;
  // Both levels are accepted rather than one: the tag sits at the top of the
  // body when the lock middleware rejects the write, and inside `error` when a
  // handler does. Reading only `error` missed every middleware rejection, which
  // is the common case.
  const isCodeShape =
    body.code === PERIOD_CLOSED ||
    (Boolean(err) &&
      typeof err === "object" &&
      !Array.isArray(err) &&
      err.code === PERIOD_CLOSED);

  if (!isCodeShape && !flattenStrings(err).includes(PERIOD_CLOSED)) return null;

  const rows = getPeriodClosedRows(error);

  return {
    code: PERIOD_CLOSED,
    closedThrough: body.closedThrough || err?.closedThrough || null,
    message: Object.values(rows).find(Boolean) || body.message || null,
    rows,
  };
};

/**
 * Does this report overlap a period that was reopened after it was closed?
 *
 * One reopen hands back the span `(closedThrough, previousClosedThrough]` — the
 * stretch that was closed and is now writable again. A full reopen (`closedThrough`
 * null) hands back everything up to `previousClosedThrough`. A report covering
 * `[start, end]` is affected when that span overlaps it: the numbers on screen may
 * differ from a copy printed before the reopen.
 *
 * `start` may be null for as-of reports (Balance Sheet, Capital Statement), whose
 * range runs from the beginning of the book to `end`.
 *
 * Rows are matched by *shape* rather than by trusting an event name: only a reopen
 * moves the boundary backwards, so `closedThrough < previousClosedThrough` identifies
 * one on its own. `event === "CLOSED"` is still excluded up front because that is the
 * one value this codebase has actually seen — same test PeriodClosePanel makes.
 *
 * Comparison goes through dayjs().valueOf() rather than string ordering: the embed
 * pages read their dates from query params that are not guaranteed to be full ISO,
 * where a lexicographic compare would quietly give the wrong answer.
 */
export const getSupersedeInfo = (history, startDate, endDate) => {
  const end = dayjs(endDate);
  if (!Array.isArray(history) || history.length === 0 || !end.isValid()) return null;

  const start = startDate ? dayjs(startDate) : null;
  const startMs = start && start.isValid() ? start.valueOf() : -Infinity;
  const endMs = end.valueOf();

  const events = history.filter((event) => {
    if (!event || event.event === "CLOSED" || !event.previousClosedThrough) return false;

    const until = dayjs(event.previousClosedThrough);
    if (!until.isValid() || until.valueOf() < startMs) return false;

    // A full reopen has no lower bound, so it reaches every report up to `until`.
    if (!event.closedThrough) return true;

    const from = dayjs(event.closedThrough);
    if (!from.isValid()) return true;

    // An empty or inverted span is a close, not a reopen — it gave nothing back.
    return from.valueOf() < until.valueOf() && from.valueOf() < endMs;
  });

  if (events.length === 0) return null;

  // The order of `history` is the backend's to choose, so the most recent reopen is
  // found rather than assumed to be first.
  const lastAt = events.reduce((latest, event) => {
    const at = dayjs(event.createdAt);
    if (!at.isValid()) return latest;
    return !latest || at.valueOf() > dayjs(latest).valueOf() ? event.createdAt : latest;
  }, null);

  return { count: events.length, lastAt };
};
